import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { isModuleEnabled } from '../config/branding';
import {
  FileText,
  Building2,
  Palette,
  Hash,
  Globe,
  Printer,
  Save,
  Upload,
  Eye,
  Trash2,
  Plus,
  Check,
  Sparkles,
  RefreshCw,
  Download,
  Copy,
  Image as ImageIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DocumentTemplates() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    document_type: 'all',
    branding: {
      logo_url: '',
      company_name: '',
      company_name_ar: '',
      address: '',
      address_ar: '',
      city: '',
      country: '',
      phone: '',
      email: '',
      website: '',
      vat_number: '',
      cr_number: '',
      primary_color: '#3b82f6',
      secondary_color: '#8b5cf6',
      accent_color: '#10b981'
    },
    layout: {
      header_style: 'modern',
      show_logo: true,
      show_company_address: true,
      show_vat_number: true,
      show_cr_number: false,
      show_item_description: true,
      show_item_code: false,
      show_discount_column: true,
      show_tax_column: true,
      show_unit_column: true,
      footer_text: '',
      footer_text_ar: '',
      terms_conditions: '',
      terms_conditions_ar: '',
      bank_details: '',
      bank_details_ar: ''
    },
    numbering: {
      quotation_prefix: 'QUO',
      quotation_separator: '-',
      quotation_digits: 4,
      quotation_include_year: true,
      quotation_include_month: false,
      invoice_prefix: 'INV',
      invoice_separator: '-',
      invoice_digits: 4,
      invoice_include_year: true,
      invoice_include_month: false,
      sales_order_prefix: 'SO',
      sales_order_separator: '-',
      sales_order_digits: 4,
      sales_order_include_year: true,
      sales_order_include_month: false,
      reset_yearly: true,
      reset_monthly: false
    },
    language_currency: {
      default_language: 'en',
      show_bilingual: false,
      default_currency: 'USD',
      currency_position: 'before',
      decimal_places: 2,
      thousand_separator: ',',
      decimal_separator: '.',
      date_format: 'MM/DD/YYYY',
      show_hijri_date: false,
      default_tax_rate: 0
    },
    print_settings: {
      paper_size: 'Letter',
      orientation: 'portrait',
      margin_top: 20,
      margin_bottom: 20,
      margin_left: 15,
      margin_right: 15,
      font_family: 'default',
      font_size: 10,
      header_font_size: 24,
      line_height: 1.5
    }
  });

  useEffect(() => {
    fetchTemplates();
    fetchPresets();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/templates/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setTemplates(data.templates || []);
      
      if (data.templates?.length > 0) {
        const defaultTemplate = data.templates.find(t => t.is_default) || data.templates[0];
        setSelectedTemplate(defaultTemplate);
        setFormData(prev => ({ ...prev, ...defaultTemplate }));
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPresets = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/templates/presets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setPresets(data.presets || {});
    } catch (error) {
      console.error('Error fetching presets:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = selectedTemplate?.id 
        ? `${API_URL}/api/templates/${selectedTemplate.id}`
        : `${API_URL}/api/templates/`;
      
      const res = await fetch(url, {
        method: selectedTemplate?.id ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast({
          title: isRTL ? 'تم الحفظ' : 'Saved',
          description: isRTL ? 'تم حفظ القالب بنجاح' : 'Template saved successfully'
        });
        fetchTemplates();
      }
    } catch (error) {
      toast({
        title: isRTL ? 'خطأ' : 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/templates/upload-logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      const data = await res.json();
      if (res.ok) {
        setFormData(prev => ({
          ...prev,
          branding: { ...prev.branding, logo_url: data.logo_url }
        }));
        toast({
          title: isRTL ? 'تم الرفع' : 'Uploaded',
          description: isRTL ? 'تم رفع الشعار بنجاح' : 'Logo uploaded successfully'
        });
      }
    } catch (error) {
      toast({
        title: isRTL ? 'خطأ' : 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const applyPreset = async (presetName) => {
    if (!selectedTemplate?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/templates/apply-preset/${presetName}?template_id=${selectedTemplate.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({ ...prev, ...data.template }));
        toast({
          title: isRTL ? 'تم التطبيق' : 'Applied',
          description: isRTL ? 'تم تطبيق النمط بنجاح' : `Preset "${presetName}" applied`
        });
      }
    } catch (error) {
      toast({
        title: isRTL ? 'خطأ' : 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const generatePreviewNumber = (type) => {
    const n = formData.numbering;
    const now = new Date();
    let prefix, separator, digits, includeYear, includeMonth;
    
    if (type === 'quotation') {
      prefix = n.quotation_prefix;
      separator = n.quotation_separator;
      digits = n.quotation_digits;
      includeYear = n.quotation_include_year;
      includeMonth = n.quotation_include_month;
    } else if (type === 'invoice') {
      prefix = n.invoice_prefix;
      separator = n.invoice_separator;
      digits = n.invoice_digits;
      includeYear = n.invoice_include_year;
      includeMonth = n.invoice_include_month;
    } else {
      prefix = n.sales_order_prefix;
      separator = n.sales_order_separator;
      digits = n.sales_order_digits;
      includeYear = n.sales_order_include_year;
      includeMonth = n.sales_order_include_month;
    }

    const parts = [prefix];
    if (includeYear) parts.push(now.getFullYear());
    if (includeMonth) parts.push(String(now.getMonth() + 1).padStart(2, '0'));
    parts.push('1'.padStart(digits, '0'));
    
    return parts.join(separator);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className={`p-6 space-y-6 ${isRTL ? 'rtl' : ''}`}>
      {/* Header */}
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h1 className="text-3xl font-bold">{isRTL ? 'قوالب المستندات' : 'Document Templates'}</h1>
          <p className="text-muted-foreground mt-1">
            {isRTL ? 'تخصيص تنسيق عروض الأسعار والفواتير' : 'Customize your quotation and invoice formats'}
          </p>
        </div>
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Eye className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'معاينة' : 'Preview'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
              <DialogHeader>
                <DialogTitle>{isRTL ? 'معاينة القالب' : 'Template Preview'}</DialogTitle>
              </DialogHeader>
              <DocumentPreview template={formData} isRTL={isRTL} />
            </DialogContent>
          </Dialog>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <RefreshCw className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
            ) : (
              <Save className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            )}
            {isRTL ? 'حفظ' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Preset Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Sparkles className="h-5 w-5 text-purple-500" />
            {isRTL ? 'أنماط جاهزة' : 'Quick Presets'}
          </CardTitle>
          <CardDescription>
            {isRTL ? 'اختر نمط جاهز للبدء السريع' : 'Choose a preset to get started quickly'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className={`flex flex-wrap gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {Object.entries(presets).map(([key, preset]) => (
              <Button
                key={key}
                variant="outline"
                className="group hover:border-purple-500"
                onClick={() => applyPreset(key)}
              >
                <div
                  className="h-4 w-4 rounded-full mr-2"
                  style={{ backgroundColor: preset.branding?.primary_color || '#3b82f6' }}
                />
                {isRTL ? preset.name_ar : preset.name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Main Settings Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className={`grid grid-cols-5 w-full ${isRTL ? 'direction-rtl' : ''}`}>
          <TabsTrigger value="branding" className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">{isRTL ? 'الهوية' : 'Branding'}</span>
          </TabsTrigger>
          <TabsTrigger value="layout" className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">{isRTL ? 'التخطيط' : 'Layout'}</span>
          </TabsTrigger>
          <TabsTrigger value="numbering" className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Hash className="h-4 w-4" />
            <span className="hidden sm:inline">{isRTL ? 'الترقيم' : 'Numbering'}</span>
          </TabsTrigger>
          <TabsTrigger value="language" className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline">{isRTL ? 'اللغة' : 'Language'}</span>
          </TabsTrigger>
          <TabsTrigger value="print" className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Printer className="h-4 w-4" />
            <span className="hidden sm:inline">{isRTL ? 'الطباعة' : 'Print'}</span>
          </TabsTrigger>
        </TabsList>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Logo Upload */}
            <Card>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <ImageIcon className="h-5 w-5" />
                  {isRTL ? 'شعار الشركة' : 'Company Logo'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  {formData.branding.logo_url ? (
                    <div className="space-y-4">
                      <img
                        src={`${API_URL}${formData.branding.logo_url}`}
                        alt="Logo"
                        className="max-h-24 mx-auto"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, logo_url: '' }
                        }))}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {isRTL ? 'إزالة' : 'Remove'}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="h-10 w-10 mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {isRTL ? 'اسحب الشعار هنا أو' : 'Drag logo here or'}
                      </p>
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept="image/*"
                        onChange={handleLogoUpload}
                      />
                      <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                        {isRTL ? 'اختر ملف' : 'Choose File'}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Colors */}
            <Card>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Palette className="h-5 w-5" />
                  {isRTL ? 'الألوان' : 'Colors'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>{isRTL ? 'اللون الرئيسي' : 'Primary'}</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={formData.branding.primary_color}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, primary_color: e.target.value }
                        }))}
                        className="h-10 w-full rounded cursor-pointer"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>{isRTL ? 'اللون الثانوي' : 'Secondary'}</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={formData.branding.secondary_color}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, secondary_color: e.target.value }
                        }))}
                        className="h-10 w-full rounded cursor-pointer"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>{isRTL ? 'لون التمييز' : 'Accent'}</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={formData.branding.accent_color}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, accent_color: e.target.value }
                        }))}
                        className="h-10 w-full rounded cursor-pointer"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Company Information */}
          <Card>
            <CardHeader>
              <CardTitle>{isRTL ? 'معلومات الشركة' : 'Company Information'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`grid ${isModuleEnabled('arabicLanguage') ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-6`}>
                <div className="space-y-4">
                  <div>
                    <Label>{isRTL ? 'اسم الشركة (إنجليزي)' : 'Company Name'}</Label>
                    <Input
                      value={formData.branding.company_name}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        branding: { ...prev.branding, company_name: e.target.value }
                      }))}
                      placeholder="Your Company Name"
                    />
                  </div>
                  <div>
                    <Label>{isRTL ? 'العنوان (إنجليزي)' : 'Address'}</Label>
                    <Textarea
                      value={formData.branding.address}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        branding: { ...prev.branding, address: e.target.value }
                      }))}
                      placeholder="123 Business Street, Suite 100"
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{isRTL ? 'المدينة' : 'City'}</Label>
                      <Input
                        value={formData.branding.city}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, city: e.target.value }
                        }))}
                        placeholder="Riyadh"
                      />
                    </div>
                    <div>
                      <Label>{isRTL ? 'الدولة' : 'Country'}</Label>
                      <Input
                        value={formData.branding.country}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, country: e.target.value }
                        }))}
                        placeholder="Saudi Arabia"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{isRTL ? 'الهاتف' : 'Phone'}</Label>
                      <Input
                        value={formData.branding.phone}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, phone: e.target.value }
                        }))}
                        placeholder="+966 xx xxx xxxx"
                      />
                    </div>
                    <div>
                      <Label>{isRTL ? 'البريد الإلكتروني' : 'Email'}</Label>
                      <Input
                        value={formData.branding.email}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          branding: { ...prev.branding, email: e.target.value }
                        }))}
                        placeholder="info@company.com"
                      />
                    </div>
                  </div>
                </div>
                {isModuleEnabled('arabicLanguage') && (
                <div className="space-y-4">
                  <div>
                    <Label>{isRTL ? 'اسم الشركة (عربي)' : 'Company Name (Arabic)'}</Label>
                    <Input
                      value={formData.branding.company_name_ar}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        branding: { ...prev.branding, company_name_ar: e.target.value }
                      }))}
                      placeholder="اسم الشركة"
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <Label>{isRTL ? 'العنوان (عربي)' : 'Address (Arabic)'}</Label>
                    <Textarea
                      value={formData.branding.address_ar}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        branding: { ...prev.branding, address_ar: e.target.value }
                      }))}
                      placeholder="العنوان بالعربي"
                      rows={2}
                      dir="rtl"
                    />
                  </div>
                </div>
                )}
              </div>
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div>
                  <Label>{isRTL ? 'الموقع الإلكتروني' : 'Website'}</Label>
                  <Input
                    value={formData.branding.website}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      branding: { ...prev.branding, website: e.target.value }
                    }))}
                    placeholder="www.company.com"
                  />
                </div>
                <div>
                  <Label>{isRTL ? 'الرقم الضريبي' : 'Tax ID / EIN'}</Label>
                  <Input
                    value={formData.branding.vat_number}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      branding: { ...prev.branding, vat_number: e.target.value }
                    }))}
                    placeholder="12-3456789"
                  />
                </div>
                <div>
                  <Label>{isRTL ? 'السجل التجاري' : 'Business Reg. No.'}</Label>
                  <Input
                    value={formData.branding.cr_number}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      branding: { ...prev.branding, cr_number: e.target.value }
                    }))}
                    placeholder="LLC-123456"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Layout Tab */}
        <TabsContent value="layout" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'نمط الرأس' : 'Header Style'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Select
                  value={formData.layout.header_style}
                  onValueChange={(value) => setFormData(prev => ({
                    ...prev,
                    layout: { ...prev.layout, header_style: value }
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="modern">{isRTL ? 'عصري' : 'Modern'}</SelectItem>
                    <SelectItem value="classic">{isRTL ? 'كلاسيكي' : 'Classic'}</SelectItem>
                    <SelectItem value="minimal">{isRTL ? 'بسيط' : 'Minimal'}</SelectItem>
                  </SelectContent>
                </Select>

                <div className="space-y-3">
                  <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Label>{isRTL ? 'إظهار الشعار' : 'Show Logo'}</Label>
                    <Switch
                      checked={formData.layout.show_logo}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        layout: { ...prev.layout, show_logo: checked }
                      }))}
                    />
                  </div>
                  <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Label>{isRTL ? 'إظهار العنوان' : 'Show Address'}</Label>
                    <Switch
                      checked={formData.layout.show_company_address}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        layout: { ...prev.layout, show_company_address: checked }
                      }))}
                    />
                  </div>
                  <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Label>{isRTL ? 'إظهار الرقم الضريبي' : 'Show Tax ID'}</Label>
                    <Switch
                      checked={formData.layout.show_vat_number}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        layout: { ...prev.layout, show_vat_number: checked }
                      }))}
                    />
                  </div>
                  <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Label>{isRTL ? 'إظهار السجل التجاري' : 'Show Business Reg.'}</Label>
                    <Switch
                      checked={formData.layout.show_cr_number}
                      onCheckedChange={(checked) => setFormData(prev => ({
                        ...prev,
                        layout: { ...prev.layout, show_cr_number: checked }
                      }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'أعمدة الجدول' : 'Table Columns'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Label>{isRTL ? 'وصف المنتج' : 'Item Description'}</Label>
                  <Switch
                    checked={formData.layout.show_item_description}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      layout: { ...prev.layout, show_item_description: checked }
                    }))}
                  />
                </div>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Label>{isRTL ? 'رمز المنتج' : 'Item Code'}</Label>
                  <Switch
                    checked={formData.layout.show_item_code}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      layout: { ...prev.layout, show_item_code: checked }
                    }))}
                  />
                </div>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Label>{isRTL ? 'عمود الوحدة' : 'Unit Column'}</Label>
                  <Switch
                    checked={formData.layout.show_unit_column}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      layout: { ...prev.layout, show_unit_column: checked }
                    }))}
                  />
                </div>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Label>{isRTL ? 'عمود الخصم' : 'Discount Column'}</Label>
                  <Switch
                    checked={formData.layout.show_discount_column}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      layout: { ...prev.layout, show_discount_column: checked }
                    }))}
                  />
                </div>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Label>{isRTL ? 'عمود الضريبة' : 'Tax Column'}</Label>
                  <Switch
                    checked={formData.layout.show_tax_column}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      layout: { ...prev.layout, show_tax_column: checked }
                    }))}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Footer & Terms */}
          <Card>
            <CardHeader>
              <CardTitle>{isRTL ? 'التذييل والشروط' : 'Footer & Terms'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`grid ${isModuleEnabled('arabicLanguage') ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-6`}>
                <div className="space-y-4">
                  <div>
                    <Label>{isRTL ? 'نص التذييل (إنجليزي)' : 'Footer Text (English)'}</Label>
                    <Textarea
                      value={formData.layout.footer_text}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        layout: { ...prev.layout, footer_text: e.target.value }
                      }))}
                      placeholder="Thank you for your business!"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>{isRTL ? 'الشروط والأحكام (إنجليزي)' : 'Terms & Conditions (English)'}</Label>
                    <Textarea
                      value={formData.layout.terms_conditions}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        layout: { ...prev.layout, terms_conditions: e.target.value }
                      }))}
                      placeholder="Payment is due within 30 days..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>{isRTL ? 'تفاصيل البنك (إنجليزي)' : 'Bank Details (English)'}</Label>
                    <Textarea
                      value={formData.layout.bank_details}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        layout: { ...prev.layout, bank_details: e.target.value }
                      }))}
                      placeholder="Bank: XXX&#10;IBAN: SA..."
                      rows={3}
                    />
                  </div>
                </div>
                {isModuleEnabled('arabicLanguage') && (
                <div className="space-y-4">
                  <div>
                    <Label>{isRTL ? 'نص التذييل (عربي)' : 'Footer Text (Arabic)'}</Label>
                    <Textarea
                      value={formData.layout.footer_text_ar}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        layout: { ...prev.layout, footer_text_ar: e.target.value }
                      }))}
                      placeholder="شكراً لتعاملكم معنا!"
                      rows={2}
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <Label>{isRTL ? 'الشروط والأحكام (عربي)' : 'Terms & Conditions (Arabic)'}</Label>
                    <Textarea
                      value={formData.layout.terms_conditions_ar}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        layout: { ...prev.layout, terms_conditions_ar: e.target.value }
                      }))}
                      placeholder="يستحق الدفع خلال 30 يوماً..."
                      rows={3}
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <Label>{isRTL ? 'تفاصيل البنك (عربي)' : 'Bank Details (Arabic)'}</Label>
                    <Textarea
                      value={formData.layout.bank_details_ar}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        layout: { ...prev.layout, bank_details_ar: e.target.value }
                      }))}
                      placeholder="البنك: XXX&#10;آيبان: SA..."
                      rows={3}
                      dir="rtl"
                    />
                  </div>
                </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Numbering Tab */}
        <TabsContent value="numbering" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Quotation Numbering */}
            <Card>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <FileText className="h-5 w-5 text-blue-500" />
                  {isRTL ? 'عروض الأسعار' : 'Quotations'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{isRTL ? 'البادئة' : 'Prefix'}</Label>
                  <Input
                    value={formData.numbering.quotation_prefix}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, quotation_prefix: e.target.value }
                    }))}
                    placeholder="QUO"
                  />
                </div>
                <div>
                  <Label>{isRTL ? 'الفاصل' : 'Separator'}</Label>
                  <Select
                    value={formData.numbering.quotation_separator}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, quotation_separator: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-">Dash (-)</SelectItem>
                      <SelectItem value="/">Slash (/)</SelectItem>
                      <SelectItem value=".">Dot (.)</SelectItem>
                      <SelectItem value="_">Underscore (_)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{isRTL ? 'عدد الأرقام' : 'Digits'}</Label>
                  <Select
                    value={String(formData.numbering.quotation_digits)}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, quotation_digits: parseInt(value) }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 (001)</SelectItem>
                      <SelectItem value="4">4 (0001)</SelectItem>
                      <SelectItem value="5">5 (00001)</SelectItem>
                      <SelectItem value="6">6 (000001)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Label>{isRTL ? 'إضافة السنة' : 'Include Year'}</Label>
                  <Switch
                    checked={formData.numbering.quotation_include_year}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, quotation_include_year: checked }
                    }))}
                  />
                </div>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Label>{isRTL ? 'إضافة الشهر' : 'Include Month'}</Label>
                  <Switch
                    checked={formData.numbering.quotation_include_month}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, quotation_include_month: checked }
                    }))}
                  />
                </div>
                <div className="pt-2 border-t">
                  <Label className="text-muted-foreground">{isRTL ? 'معاينة' : 'Preview'}</Label>
                  <div className="mt-1 p-2 bg-muted rounded font-mono text-sm">
                    {generatePreviewNumber('quotation')}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Invoice Numbering */}
            <Card>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <FileText className="h-5 w-5 text-emerald-500" />
                  {isRTL ? 'الفواتير' : 'Invoices'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{isRTL ? 'البادئة' : 'Prefix'}</Label>
                  <Input
                    value={formData.numbering.invoice_prefix}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, invoice_prefix: e.target.value }
                    }))}
                    placeholder="INV"
                  />
                </div>
                <div>
                  <Label>{isRTL ? 'الفاصل' : 'Separator'}</Label>
                  <Select
                    value={formData.numbering.invoice_separator}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, invoice_separator: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-">Dash (-)</SelectItem>
                      <SelectItem value="/">Slash (/)</SelectItem>
                      <SelectItem value=".">Dot (.)</SelectItem>
                      <SelectItem value="_">Underscore (_)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{isRTL ? 'عدد الأرقام' : 'Digits'}</Label>
                  <Select
                    value={String(formData.numbering.invoice_digits)}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, invoice_digits: parseInt(value) }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 (001)</SelectItem>
                      <SelectItem value="4">4 (0001)</SelectItem>
                      <SelectItem value="5">5 (00001)</SelectItem>
                      <SelectItem value="6">6 (000001)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Label>{isRTL ? 'إضافة السنة' : 'Include Year'}</Label>
                  <Switch
                    checked={formData.numbering.invoice_include_year}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, invoice_include_year: checked }
                    }))}
                  />
                </div>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Label>{isRTL ? 'إضافة الشهر' : 'Include Month'}</Label>
                  <Switch
                    checked={formData.numbering.invoice_include_month}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, invoice_include_month: checked }
                    }))}
                  />
                </div>
                <div className="pt-2 border-t">
                  <Label className="text-muted-foreground">{isRTL ? 'معاينة' : 'Preview'}</Label>
                  <div className="mt-1 p-2 bg-muted rounded font-mono text-sm">
                    {generatePreviewNumber('invoice')}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sales Order Numbering */}
            <Card>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <FileText className="h-5 w-5 text-purple-500" />
                  {isRTL ? 'طلبات البيع' : 'Sales Orders'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{isRTL ? 'البادئة' : 'Prefix'}</Label>
                  <Input
                    value={formData.numbering.sales_order_prefix}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, sales_order_prefix: e.target.value }
                    }))}
                    placeholder="SO"
                  />
                </div>
                <div>
                  <Label>{isRTL ? 'الفاصل' : 'Separator'}</Label>
                  <Select
                    value={formData.numbering.sales_order_separator}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, sales_order_separator: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-">Dash (-)</SelectItem>
                      <SelectItem value="/">Slash (/)</SelectItem>
                      <SelectItem value=".">Dot (.)</SelectItem>
                      <SelectItem value="_">Underscore (_)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{isRTL ? 'عدد الأرقام' : 'Digits'}</Label>
                  <Select
                    value={String(formData.numbering.sales_order_digits)}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, sales_order_digits: parseInt(value) }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 (001)</SelectItem>
                      <SelectItem value="4">4 (0001)</SelectItem>
                      <SelectItem value="5">5 (00001)</SelectItem>
                      <SelectItem value="6">6 (000001)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Label>{isRTL ? 'إضافة السنة' : 'Include Year'}</Label>
                  <Switch
                    checked={formData.numbering.sales_order_include_year}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, sales_order_include_year: checked }
                    }))}
                  />
                </div>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Label>{isRTL ? 'إضافة الشهر' : 'Include Month'}</Label>
                  <Switch
                    checked={formData.numbering.sales_order_include_month}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, sales_order_include_month: checked }
                    }))}
                  />
                </div>
                <div className="pt-2 border-t">
                  <Label className="text-muted-foreground">{isRTL ? 'معاينة' : 'Preview'}</Label>
                  <div className="mt-1 p-2 bg-muted rounded font-mono text-sm">
                    {generatePreviewNumber('sales_order')}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Reset Options */}
          <Card>
            <CardHeader>
              <CardTitle>{isRTL ? 'خيارات إعادة التعيين' : 'Reset Options'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`flex items-center gap-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Switch
                    checked={formData.numbering.reset_yearly}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, reset_yearly: checked }
                    }))}
                  />
                  <Label>{isRTL ? 'إعادة تعيين سنوياً' : 'Reset Yearly'}</Label>
                </div>
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Switch
                    checked={formData.numbering.reset_monthly}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      numbering: { ...prev.numbering, reset_monthly: checked }
                    }))}
                  />
                  <Label>{isRTL ? 'إعادة تعيين شهرياً' : 'Reset Monthly'}</Label>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Language Tab */}
        <TabsContent value="language" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'إعدادات اللغة' : 'Language Settings'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{isRTL ? 'اللغة الافتراضية' : 'Default Language'}</Label>
                  <Select
                    value={formData.language_currency.default_language}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      language_currency: { ...prev.language_currency, default_language: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">{isRTL ? 'إنجليزي' : 'English'}</SelectItem>
                      <SelectItem value="ar">{isRTL ? 'عربي' : 'Arabic'}</SelectItem>
                      <SelectItem value="both">{isRTL ? 'كلاهما' : 'Both'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Label>{isRTL ? 'عرض ثنائي اللغة' : 'Show Bilingual'}</Label>
                  <Switch
                    checked={formData.language_currency.show_bilingual}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      language_currency: { ...prev.language_currency, show_bilingual: checked }
                    }))}
                  />
                </div>
                <div>
                  <Label>{isRTL ? 'تنسيق التاريخ' : 'Date Format'}</Label>
                  <Select
                    value={formData.language_currency.date_format}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      language_currency: { ...prev.language_currency, date_format: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (23/12/2024)</SelectItem>
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (12/23/2024)</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (2024-12-23)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Label>{isRTL ? 'عرض التاريخ الهجري' : 'Show Hijri Date'}</Label>
                  <Switch
                    checked={formData.language_currency.show_hijri_date}
                    onCheckedChange={(checked) => setFormData(prev => ({
                      ...prev,
                      language_currency: { ...prev.language_currency, show_hijri_date: checked }
                    }))}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'إعدادات العملة' : 'Currency Settings'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{isRTL ? 'العملة الافتراضية' : 'Default Currency'}</Label>
                  <Select
                    value={formData.language_currency.default_currency}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      language_currency: { ...prev.language_currency, default_currency: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SAR">ر.س SAR - Saudi Riyal</SelectItem>
                      <SelectItem value="AED">د.إ AED - UAE Dirham</SelectItem>
                      <SelectItem value="USD">$ USD - US Dollar</SelectItem>
                      <SelectItem value="EUR">€ EUR - Euro</SelectItem>
                      <SelectItem value="GBP">£ GBP - British Pound</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{isRTL ? 'موضع العملة' : 'Currency Position'}</Label>
                  <Select
                    value={formData.language_currency.currency_position}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      language_currency: { ...prev.language_currency, currency_position: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="before">{isRTL ? 'قبل (ر.س 100)' : 'Before ($100)'}</SelectItem>
                      <SelectItem value="after">{isRTL ? 'بعد (100 ر.س)' : 'After (100$)'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{isRTL ? 'الخانات العشرية' : 'Decimal Places'}</Label>
                    <Select
                      value={String(formData.language_currency.decimal_places)}
                      onValueChange={(value) => setFormData(prev => ({
                        ...prev,
                        language_currency: { ...prev.language_currency, decimal_places: parseInt(value) }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="3">3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{isRTL ? 'فاصل الآلاف' : 'Thousand Separator'}</Label>
                    <Select
                      value={formData.language_currency.thousand_separator}
                      onValueChange={(value) => setFormData(prev => ({
                        ...prev,
                        language_currency: { ...prev.language_currency, thousand_separator: value }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value=",">, (Comma)</SelectItem>
                        <SelectItem value=".">. (Dot)</SelectItem>
                        <SelectItem value=" ">Space</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="pt-4 border-t">
                  <Label>{isRTL ? 'نسبة الضريبة الافتراضية (%)' : 'Default Tax Rate (%)'}</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.language_currency.default_tax_rate || 0}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      language_currency: { ...prev.language_currency, default_tax_rate: parseFloat(e.target.value) || 0 }
                    }))}
                    placeholder="0"
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {isRTL ? 'أدخل 0 لإخفاء الضريبة' : 'Enter 0 to hide tax on invoices'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Print Tab */}
        <TabsContent value="print" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'إعدادات الصفحة' : 'Page Settings'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{isRTL ? 'حجم الورق' : 'Paper Size'}</Label>
                  <Select
                    value={formData.print_settings.paper_size}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      print_settings: { ...prev.print_settings, paper_size: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A4">A4 (210 × 297 mm)</SelectItem>
                      <SelectItem value="Letter">Letter (8.5 × 11 in)</SelectItem>
                      <SelectItem value="Legal">Legal (8.5 × 14 in)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{isRTL ? 'الاتجاه' : 'Orientation'}</Label>
                  <Select
                    value={formData.print_settings.orientation}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      print_settings: { ...prev.print_settings, orientation: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portrait">{isRTL ? 'عمودي' : 'Portrait'}</SelectItem>
                      <SelectItem value="landscape">{isRTL ? 'أفقي' : 'Landscape'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{isRTL ? 'هامش أعلى (مم)' : 'Margin Top (mm)'}</Label>
                    <Input
                      type="number"
                      value={formData.print_settings.margin_top}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        print_settings: { ...prev.print_settings, margin_top: parseInt(e.target.value) }
                      }))}
                    />
                  </div>
                  <div>
                    <Label>{isRTL ? 'هامش أسفل (مم)' : 'Margin Bottom (mm)'}</Label>
                    <Input
                      type="number"
                      value={formData.print_settings.margin_bottom}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        print_settings: { ...prev.print_settings, margin_bottom: parseInt(e.target.value) }
                      }))}
                    />
                  </div>
                  <div>
                    <Label>{isRTL ? 'هامش يسار (مم)' : 'Margin Left (mm)'}</Label>
                    <Input
                      type="number"
                      value={formData.print_settings.margin_left}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        print_settings: { ...prev.print_settings, margin_left: parseInt(e.target.value) }
                      }))}
                    />
                  </div>
                  <div>
                    <Label>{isRTL ? 'هامش يمين (مم)' : 'Margin Right (mm)'}</Label>
                    <Input
                      type="number"
                      value={formData.print_settings.margin_right}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        print_settings: { ...prev.print_settings, margin_right: parseInt(e.target.value) }
                      }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{isRTL ? 'إعدادات الخط' : 'Font Settings'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{isRTL ? 'نوع الخط' : 'Font Family'}</Label>
                  <Select
                    value={formData.print_settings.font_family}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      print_settings: { ...prev.print_settings, font_family: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">{isRTL ? 'افتراضي' : 'Default'}</SelectItem>
                      <SelectItem value="arial">Arial</SelectItem>
                      <SelectItem value="times">Times New Roman</SelectItem>
                      <SelectItem value="almarai">Almarai (Arabic)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{isRTL ? 'حجم الخط' : 'Font Size'}</Label>
                    <Select
                      value={String(formData.print_settings.font_size)}
                      onValueChange={(value) => setFormData(prev => ({
                        ...prev,
                        print_settings: { ...prev.print_settings, font_size: parseInt(value) }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="8">8pt</SelectItem>
                        <SelectItem value="9">9pt</SelectItem>
                        <SelectItem value="10">10pt</SelectItem>
                        <SelectItem value="11">11pt</SelectItem>
                        <SelectItem value="12">12pt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{isRTL ? 'حجم خط العنوان' : 'Header Font Size'}</Label>
                    <Select
                      value={String(formData.print_settings.header_font_size)}
                      onValueChange={(value) => setFormData(prev => ({
                        ...prev,
                        print_settings: { ...prev.print_settings, header_font_size: parseInt(value) }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="18">18pt</SelectItem>
                        <SelectItem value="20">20pt</SelectItem>
                        <SelectItem value="24">24pt</SelectItem>
                        <SelectItem value="28">28pt</SelectItem>
                        <SelectItem value="32">32pt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>{isRTL ? 'ارتفاع السطر' : 'Line Height'}</Label>
                  <Select
                    value={String(formData.print_settings.line_height)}
                    onValueChange={(value) => setFormData(prev => ({
                      ...prev,
                      print_settings: { ...prev.print_settings, line_height: parseFloat(value) }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">{isRTL ? 'ضيق' : 'Tight'} (1.0)</SelectItem>
                      <SelectItem value="1.25">{isRTL ? 'عادي' : 'Normal'} (1.25)</SelectItem>
                      <SelectItem value="1.5">{isRTL ? 'مريح' : 'Relaxed'} (1.5)</SelectItem>
                      <SelectItem value="2">{isRTL ? 'واسع' : 'Loose'} (2.0)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Document Preview Component
function DocumentPreview({ template, isRTL }) {
  const { branding, layout, language_currency } = template;
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  return (
    <div className="bg-white text-black p-8 rounded-lg shadow-lg" style={{ minHeight: '600px' }}>
      {/* Header */}
      <div className={`flex items-start justify-between mb-8 pb-4 border-b-2 ${isRTL ? 'flex-row-reverse' : ''}`} style={{ borderColor: branding.primary_color }}>
        <div className={isRTL ? 'text-right' : ''}>
          {layout.show_logo && branding.logo_url && (
            <div className="mb-2">
              <img 
                src={branding.logo_url.startsWith('http') ? branding.logo_url : `${API_URL}${branding.logo_url}`}
                alt="Company Logo"
                className="h-16 max-w-[150px] object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          <h1 className="text-2xl font-bold" style={{ color: branding.primary_color }}>
            {language_currency.show_bilingual ? (
              <>
                {branding.company_name}
                {branding.company_name_ar && <span className="block text-lg">{branding.company_name_ar}</span>}
              </>
            ) : (
              branding.company_name || 'Company Name'
            )}
          </h1>
          {layout.show_company_address && (
            <p className="text-sm text-gray-600 mt-1">
              {branding.address || '123 Business Street'}<br />
              {branding.city || 'City'}, {branding.country || 'Country'}
            </p>
          )}
        </div>
        <div className={`${isRTL ? 'text-left' : 'text-right'}`}>
          <h2 className="text-3xl font-bold" style={{ color: branding.primary_color }}>
            {isRTL ? 'فاتورة' : 'INVOICE'}
          </h2>
          <p className="text-sm text-gray-600">INV-2024-0001</p>
          <p className="text-sm text-gray-600">{new Date().toLocaleDateString()}</p>
          {layout.show_vat_number && branding.vat_number && (
            <p className="text-xs text-gray-500 mt-2">Tax ID: {branding.vat_number}</p>
          )}
        </div>
      </div>

      {/* Client Info */}
      <div className="mb-6">
        <h3 className="font-semibold mb-2" style={{ color: branding.secondary_color }}>
          {isRTL ? 'فاتورة إلى:' : 'Bill To:'}
        </h3>
        <p className="text-sm">Client Name</p>
        <p className="text-sm text-gray-600">Client Address</p>
      </div>

      {/* Items Table */}
      <table className="w-full mb-6 text-sm">
        <thead>
          <tr style={{ backgroundColor: branding.primary_color }} className="text-white">
            <th className="p-2 text-left">#</th>
            <th className="p-2 text-left">{isRTL ? 'الوصف' : 'Description'}</th>
            {layout.show_unit_column && <th className="p-2 text-center">{isRTL ? 'الوحدة' : 'Unit'}</th>}
            <th className="p-2 text-center">{isRTL ? 'الكمية' : 'Qty'}</th>
            <th className="p-2 text-right">{isRTL ? 'السعر' : 'Price'}</th>
            {layout.show_discount_column && <th className="p-2 text-right">{isRTL ? 'الخصم' : 'Disc.'}</th>}
            {layout.show_tax_column && language_currency.default_tax_rate > 0 && <th className="p-2 text-right">{isRTL ? 'الضريبة' : 'Tax'}</th>}
            <th className="p-2 text-right">{isRTL ? 'المجموع' : 'Total'}</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b">
            <td className="p-2">1</td>
            <td className="p-2">Sample Product</td>
            {layout.show_unit_column && <td className="p-2 text-center">Pcs</td>}
            <td className="p-2 text-center">10</td>
            <td className="p-2 text-right">100.00</td>
            {layout.show_discount_column && <td className="p-2 text-right">0.00</td>}
            {layout.show_tax_column && language_currency.default_tax_rate > 0 && <td className="p-2 text-right">{language_currency.default_tax_rate}%</td>}
            <td className="p-2 text-right font-semibold">{(1000 * (1 + (language_currency.default_tax_rate || 0) / 100)).toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      {/* Totals */}
      <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'}`}>
        <div className="w-64">
          <div className="flex justify-between py-1">
            <span>{isRTL ? 'المجموع الفرعي:' : 'Subtotal:'}</span>
            <span>1,000.00</span>
          </div>
          {language_currency.default_tax_rate > 0 && (
            <div className="flex justify-between py-1">
              <span>{isRTL ? `الضريبة (${language_currency.default_tax_rate}%):` : `Tax (${language_currency.default_tax_rate}%):`}</span>
              <span>{(1000 * (language_currency.default_tax_rate / 100)).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between py-2 border-t-2 font-bold" style={{ borderColor: branding.primary_color }}>
            <span>{isRTL ? 'الإجمالي:' : 'Total:'}</span>
            <span style={{ color: branding.primary_color }}>
              {language_currency.default_currency} {(1000 * (1 + (language_currency.default_tax_rate || 0) / 100)).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Footer */}
      {(layout.footer_text || layout.terms_conditions || layout.bank_details) && (
        <div className="mt-8 pt-4 border-t text-xs text-gray-600">
          {layout.terms_conditions && (
            <div className="mb-2">
              <strong>{isRTL ? 'الشروط والأحكام:' : 'Terms & Conditions:'}</strong>
              <p>{layout.terms_conditions}</p>
            </div>
          )}
          {layout.bank_details && (
            <div className="mb-2">
              <strong>{isRTL ? 'تفاصيل البنك:' : 'Bank Details:'}</strong>
              <p className="whitespace-pre-line">{layout.bank_details}</p>
            </div>
          )}
          {layout.footer_text && (
            <p className="text-center mt-4">{layout.footer_text}</p>
          )}
        </div>
      )}
    </div>
  );
}
